<template>
  <CContainer
    class="pass-history-view appointment-pass-view datatable-container mb-5"
  >
    <!-- Status + error messages announced via the persistent global live region
         (useLiveAnnouncer) so they fire reliably; Loader stays visual-only. -->
    <Loader :is-processing="isDashboardBusy" status-announced-externally />
    <CRow class="mt-0 mt-sm-4">
      <CCol md="12">
        <div class="table-tabs-header bg-white mb-2 p-0">
          <div
            :class="{ 'show-hidden-tabs': state.showTabs }"
            id="dashboard-tabs-region"
            ref="tabsRegionRef"
            class="d-flex align-items-center justify-content-between flex-wrap"
          >
            <DashboardTabs />
            <div
              class="d-none d-sm-flex actions align-items-center pt-0 p-sm-2"
            >
              <CButton
                v-show="isActiveDashboardUpdate"
                class="py-2 px-2 me-4 btn btn-inactive btn-sockets"
                @click="pauseDashboardUpdate"
              >
                <i class="fi flaticon-pause me-2"></i>Pause School Dashboard
              </CButton>
              <CButton
                v-show="!isActiveDashboardUpdate"
                class="py-2 px-2 me-4 btn bg-gradient-red text-white btn-sockets"
                @click="resumeDashboardUpdate"
              >
                <i class="fi flaticon-play me-2"></i>Resume School Dashboard
              </CButton>
              <button
                v-if="showDownloadCSVButton"
                type="button"
                class="lh-normal text-accent-blue-light btn p-0 border-0"
                aria-label="Download dashboard CSV"
                @click="exportAsCSV()"
              >
                <i class="fi flaticon-download me-1"></i>
                <span class="d-inline-block">Download CSV</span>
              </button>
              <div
                class="d-flex align-items-center search-field mb-0 ms-2 ms-md-4"
              >
                <CInputGroup class="w-auto">
                  <div class="input-group-prepend">
                    <span class="input-group-text lh-normal"
                      ><div><i class="flaticon-magnifying-glass"></i></div
                    ></span>
                  </div>
                  <CFormInput
                    class=""
                    placeholder="Search"
                    aria-label="Search passes"
                    :value="filterData.search_query"
                    :disabled="state.isLoading"
                    @update:model-value="searchByQuery"
                  >
                  </CFormInput>
                </CInputGroup>
                <CButton
                  v-if="!isAppoinmentMode"
                  class="btn-advanced-search d-flex align-items-center"
                  :aria-expanded="
                    state.isVisibleAdvancedSearch ? 'true' : 'false'
                  "
                  aria-controls="dashboard-advanced-search"
                  @click="
                    state.isVisibleAdvancedSearch =
                      !state.isVisibleAdvancedSearch
                  "
                >
                  <i
                    class="fi me-1"
                    :class="
                      state.isVisibleAdvancedSearch
                        ? 'flaticon-minus'
                        : 'flaticon-plus'
                    "
                  ></i
                  >Advanced Search
                </CButton>
              </div>
            </div>
            <button
              class="m-auto p-2 d-block d-sm-none cursor-pointer"
              type="button"
              ref="mobileTabsToggleButtonRef"
              :aria-expanded="state.showTabs ? 'true' : 'false'"
              aria-controls="dashboard-tabs-region"
              :aria-label="
                state.showTabs
                  ? 'Show fewer dashboard controls'
                  : 'Show more dashboard controls'
              "
              @click="toggleDashboardTabs"
            >
              {{ !state.showTabs ? "Show more" : "Show less" }}
            </button>
          </div>
          <div
            v-if="!isAppoinmentMode && state.isVisibleAdvancedSearch"
            id="dashboard-advanced-search"
            class="advanced-search-container px-4 pb-3"
          >
            <CRow class="justify-content-center mt-4">
              <CCol md="4">
                <LazyLoadSelect
                  :is-multiple="true"
                  classes="mb-3"
                  :selected="filterData.by_students"
                  type="student"
                  accessible-label="Search students"
                  placeholder="Search students"
                  @changed="
                    (value) => {
                      $store.commit('dashboardTable/UPDATE_FILTERS_DATA', {
                        filter: 'by_students',
                        value: value
                      })
                    }
                  "
                />
              </CCol>
              <CCol md="4">
                <LazyLoadSelect
                  :is-multiple="true"
                  classes="mb-3"
                  :selected="filterData.by_teachers"
                  type="adults"
                  accessible-label="Search teachers"
                  placeholder="Search teachers"
                  @changed="
                    (value) => {
                      $store.commit('dashboardTable/UPDATE_FILTERS_DATA', {
                        filter: 'by_teachers',
                        value: value
                      })
                    }
                  "
                />
              </CCol>
              <CCol md="4">
                <LazyLoadSelect
                  :is-multiple="true"
                  :is-processing="false"
                  classes="mb-3"
                  :selected="filterData.by_rooms"
                  type="locations"
                  accessible-label="Search rooms"
                  placeholder="Search rooms"
                  @changed="
                    (value) => {
                      $store.commit('dashboardTable/UPDATE_FILTERS_DATA', {
                        filter: 'by_rooms',
                        value: value
                      })
                    }
                  "
                />
              </CCol>
              <CCol md="12" class="mt-1 text-center text-sm-start">
                <div
                  v-if="!isQueueActiveAndStatusSelected"
                  class="d-inline-block text-center me-2"
                >
                  <CButton
                    class="btn bg-gradient-blue search-btn text-white"
                    @click="advancedFilter()"
                  >
                    <div class="p-small">Search</div>
                  </CButton>
                </div>
                <div class="d-inline-block text-center">
                  <CButton class="btn me-2 clear-btn" @click="clearFilter()">
                    Clear
                  </CButton>
                </div>
              </CCol>
            </CRow>
          </div>
        </div>
        <template v-if="!isAppoinmentMode">
          <div
            v-if="isQueueActiveAndStatusSelected"
            class="d-flex gap-2 justify-content-between align-content-center flex-wrap my-4"
          >
            <div class="d-flex gap-4 flex-wrap">
              <DashboardStatusFilters />
              <CustomCheck
                label="Basic view"
                v-model="state.isBasicView"
                name="Basic view"
              />
              <CustomCheck
                label="Student Number"
                v-model="filterDataStudentNumber"
                name="student_sis_id"
              />
            </div>
            <div
              class="d-flex justify-content-end align-content-center gap-3 pe-1 flex-wrap"
            >
              <BaseButton
                v-if="activePassState === 'both'"
                rounded
                solid
                data-testid="clear-passes-button"
                @click="handleClearPasses"
              >
                Clear Passes
              </BaseButton>
              <BaseButton
                v-if="showClearQueueButton"
                rounded
                aria-label="Clear Queue passes"
                @click="onClearQueue"
              >
                Clear Queue
              </BaseButton>
            </div>
          </div>
          <div v-else>
            <div
              class="status-guides-container mb-2 flex-wrap d-flex justify-content-between align-items-center"
            >
              <DashboardStatusFilters />
              <div
                v-if="activePassState === 'both'"
                class="d-flex justify-content-end align-content-center gap-3 pe-1 flex-wrap"
              >
                <BaseButton
                  rounded
                  solid
                  data-testid="clear-passes-button"
                  @click="handleClearPasses"
                >
                  Clear Passes
                </BaseButton>
              </div>
              <DashboardTimeFilters />
              <div
                class="d-flex flex-column justify-content-center p-2 order-2 order-lg-2"
              >
                <div class="text-center mb-1" role="heading" aria-level="3">
                  Basic View
                </div>
                <div class="d-flex align-items-center">
                  <span class="me-2">Off</span>
                  <CFormSwitch
                    id="basicViewSwitch"
                    switch-type="custom"
                    class="cs-switch mx-1"
                    shape="pill"
                    :checked="state.isBasicView"
                    role="switch"
                    :aria-checked="state.isBasicView ? 'true' : 'false'"
                    aria-label="Toggle basic view of dashboard table"
                    @change="state.isBasicView = !state.isBasicView"
                  />
                  <span>On</span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <DataTableOptimized
          v-if="!state.isLoading && !isAppoinmentMode"
          id="dashboardTable"
          table-accessible-name="School dashboard passes"
          class="datatable bg-white"
          :class="{ 'basic-view': state.isBasicView }"
          :clickable-rows="state.isBasicView"
          add-table-classes="datatable-table dashboard-table"
          :items="dashboardPasses"
          :fields="activeDataTableFields"
          :sorter="{ external: true }"
          :no-items-view="{
            noResults: 'No items available',
            noItems: 'No items available'
          }"
          :basic-view="state.isBasicView"
          @update:sorter-value="sortByColumn"
          @row-clicked="setActiveRow"
        >
          <template #over-table>
            <HelpCenterButton
              classes="top-right mt-2 me-2"
              width="15"
              heigth="15"
              content-key="dashboard_datatable"
              :is-old="true"
            />
          </template>
          <template #student_sis_id="{ item, styles }">
            <td class="pass-time-info" :style="styles">
              {{
                item.user && item.user.student_sis_id
                  ? item.user.student_sis_id
                  : ""
              }}
            </td>
          </template>
          <template #user="{ item, styles }">
            <td :style="styles">
              <UserColumnData
                :pass="item"
                :is-basic-view="
                  state.isBasicView && state.activeTableRow.item.id !== item.id
                "
              />
            </td>
          </template>

          <template #queue_number="{ item, styles }">
            <td class="status-field" :style="styles">
              {{ item?.queue_number || 0 }}
            </td>
          </template>

          <template #badge_flags="{ item, styles }">
            <td class="status-field" :style="styles">
              <div
                v-if="getPassStatus(item)"
                class="dashboard-status-text"
                :class="`dashboard-status-text--${
                  getPassStatus(item).class || 'default'
                }`"
              >
                {{ getPassStatus(item).label }}
              </div>
            </td>
          </template>

          <template #queue_action="{ item, styles }">
            <td class="pass-time-info" :style="styles">
              <PassActionButtons
                :pass="item"
                :actions-status="999"
                :is-queue-mode="true"
              />
            </td>
          </template>

          <template #approved_at="{ item, styles }">
            <td class="pass-time-info align-top" :style="styles">
              <b>{{ item.from ? item.from.name : "" }}</b>
              <div
                v-show="
                  !state.isBasicView || state.activeTableRow.item.id === item.id
                "
              >
                <p class="my-1 mx-0 p-0">
                  {{
                    item.approved_at
                      ? $helpers.transformDate(item.approved_at, "h:mm A")
                      : ""
                  }}
                </p>
                <InOutColumn :pass="item" section="approved_at" />
                <PassActionButtons :pass="item" :actions-status="1" />
              </div>
            </td>
          </template>

          <template #completed_at="{ item, styles }">
            <td class="pass-time-info in-time align-top" :style="styles">
              <b>{{ item.to ? item.to.name : "" }}</b>
              <div
                v-show="
                  !state.isBasicView || state.activeTableRow.item.id === item.id
                "
              >
                <p class="my-1 mx-0 p-0">
                  {{
                    item.completed_at
                      ? $helpers.transformDate(item.completed_at, "h:mm A")
                      : ""
                  }}
                </p>

                <InOutColumn :pass="item" section="completed_at" />

                <PassActionButtons :pass="item" :actions-status="2" />
              </div>
            </td>
          </template>

          <template #approved_by="{ item, styles }">
            <td class="pass-time-info align-top" :style="styles">
              <b>{{
                item.child && item.child.from ? item.child.from.name : ""
              }}</b>
              <div
                v-show="
                  !state.isBasicView || state.activeTableRow.item.id === item.id
                "
              >
                <p class="my-1 mx-0 p-0">
                  {{
                    item.child && item.child.approved_at
                      ? $helpers.transformDate(item.child.approved_at, "h:mm A")
                      : ""
                  }}
                </p>

                <InOutColumn :pass="item" section="child_approved_at" />
                <PassActionButtons :pass="item" :actions-status="3" />
              </div>
            </td>
          </template>

          <template #completed_by="{ item, styles }">
            <td class="pass-time-info in-time align-top" :style="styles">
              <b>{{ item.child && item.child.to ? item.child.to.name : "" }}</b>
              <div
                v-show="
                  !state.isBasicView || state.activeTableRow.item.id === item.id
                "
              >
                <p class="my-1 mx-0 p-0">
                  {{
                    item.child && item.child.completed_at
                      ? $helpers.transformDate(
                          item.child.completed_at,
                          "h:mm A"
                        )
                      : ""
                  }}
                </p>

                <InOutColumn :pass="item" section="child_completed_at" />

                <PassActionButtons :pass="item" :actions-status="4" />
              </div>
            </td>
          </template>

          <template #total_time="{ item, styles }">
            <td class="pass-time-info in-time align-top" :style="styles">
              <b>{{
                (item.total_time && item.badge_flags.ended) ||
                item.badge_flags.system_completed
                  ? item.total_time
                  : ""
              }}</b>
            </td>
          </template>

          <template #comments="{ item, styles }">
            <td :style="styles">
              <PassComments
                :pass="item"
                :is-basic-view="
                  state.isBasicView && state.activeTableRow.item.id !== item.id
                "
                commentable-type="App\Models\Pass"
              />
            </td>
          </template>

          <template #expired_at="{ item, styles }">
            <td :style="styles">
              {{
                item.expired_at ? $helpers.transformDate(item.expired_at) : ""
              }}
            </td>
          </template>
        </DataTableOptimized>
        <DashboardDataTableAptV6 v-if="isAppoinmentMode" ref="aptPassTable" />
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import {
  reactive,
  computed,
  onMounted,
  watch,
  inject,
  ref,
  nextTick
} from "vue"
import { isAllEntriesPerPageOption } from "@/constants/tablePaginationConstants.js"
import { useStore } from "vuex"
import UserColumnData from "@/v3components/Datatables/ColumnComponents/UserColumnData.vue"
import PassComments from "@/v3components/Datatables/ColumnComponents/PassComments.vue"
import PassActionButtons from "@/v3components/shared/PassComponents/PassActionButtons.vue"
import InOutColumn from "@/v3components/shared/PassComponents/InOutColumnOld.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelectOld.vue"
import DashboardTabs from "@/v3components/Datatables/DashboardDataTable/TabsAndFilter/DashboardTabsOld.vue"
import DashboardStatusFilters from "@/v3components/Datatables/DashboardDataTable/TabsAndFilter/DashboardStatusFiltersOld.vue"
import DashboardTimeFilters from "@/v3components/Datatables/DashboardDataTable/TabsAndFilter/DashboardTimeFiltersOld.vue"
import DashboardDataTableAptV6 from "@/v3components/Datatables/DashboardDataTable/DashboardDataTableAptV6Old.vue"
import HelpCenterButton from "@/v3components/HelpCenterButton.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import CustomCheck from "@/v3components/shared/Form/CustomCheck.vue"
import useConfirmation from "@/composables/useConfirmation"
import helpers from "@/helpers/index.js"
import download from "@/helpers/downloadCSV.js"
import tableConfig from "./dashboardTableConfigOld.js"
import { syncVueSelectListboxAriaHidden } from "@/helpers/vueSelectA11y"
import queueUtils from "@/helpers/queueUtils"
import useAppToast from "@/composables/useAppToast"
import { announce } from "@/composables/useLiveAnnouncer"

export default {
  name: "DashboardDataTableOld",
  components: {
    Loader,
    UserColumnData,
    PassComments,
    PassActionButtons,
    InOutColumn,
    LazyLoadSelect,
    HelpCenterButton,
    DashboardTabs,
    DashboardStatusFilters,
    DashboardTimeFilters,
    DashboardDataTableAptV6,
    BaseButton,
    CustomCheck
  },
  setup() {
    const tabsRegionRef = ref(null)
    const mobileTabsToggleButtonRef = ref(null)

    const modal = inject("modal")

    const toast = useAppToast()
    const store = useStore()
    const { showConfirmation } = useConfirmation(modal)

    const state = reactive({
      ...tableConfig,
      isBasicView: false,
      isLazyLoadingMode: false,
      isVisibleAdvancedSearch: false,
      modal: {
        isVisible: {
          editApptPass: false
        }
      },
      activeTableRow: { item: { id: null }, index: null },
      isLoading: false,
      showTabs: false,
      isInitialLoad: true
    })

    const periods = computed(() => store.getters["periods/formListStudents"])
    const editablePass = computed(
      () => store.getters["adultAptPass/editablePass"]
    )
    const passesTimeSettings = computed(
      () => store.getters["passes/passesTimeSettings"]
    )
    const currentUser = computed(() => store.getters["authentication/user"])
    const filterData = computed(
      () => store.getters["dashboardTable/filterData"]
    )
    const filterDataStudentNumber = computed({
      get() {
        return filterData?.value?.student_sis_id ? true : false
      },
      set(value) {
        store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
          {
            filter: "student_sis_id",
            value
          }
        ])
      }
    })

    const aptFilterData = computed(
      () => store.getters["dashboardTable/aptFilterData"]
    )
    const dashboardPasses = computed(() => {
      if (isQueueActiveAndStatusSelected.value) {
        return store.getters["dashboardTable/queuePasses"]
      }
      return store.getters["dashboardTable/passes"]
    })
    const isActiveDashboardUpdate = computed(
      () => store.getters["dashboardTable/isActiveDashboardUpdate"]
    )
    const activeSchoolHasAppoitmentPasses = computed(
      () => store.getters["schools/activeSchoolHasAppoitmentPasses"]
    )
    const isUserInTransparency = computed(
      () => store.getters["authentication/isUserInTransparency"]
    )
    const stats = computed(() => store.getters["dashboardTable/stats"])
    const activePassType = computed(
      () => store.getters["dashboardTable/getType"]
    )
    const activePassState = computed(
      () => store.getters["dashboardTable/getStatus"]
    )
    const school = computed(() => store.getters["schools/activeSchool"])
    const areAppointmentPassesLoading = computed(
      () => store.getters["adultAptPass/areAppointmentPassesLoading"]
    )

    const appointmentPasses = computed(
      () => store.getters["adultAptPass/appointmentPasses"] || []
    )

    const isAppoinmentMode = computed(() => {
      return activePassType.value.includes("appointment")
    })

    const isDashboardBusy = computed(
      () => state.isLoading || areAppointmentPassesLoading.value
    )

    const activeDataTableFields = computed(() => {
      if (isQueueActiveAndStatusSelected.value) {
        return state.queueDataTableFields
          .filter((field) =>
            !state.isBasicView ? field.key !== "queue_number" : true
          )
          .filter((field) =>
            !filterData.value.student_sis_id
              ? field.key !== "student_sis_id"
              : field
          )
      }

      return state.dataTableFields
        .filter((field) =>
          !state.isBasicView ? field.key !== "badge_flags" : true
        )
        .filter((field) =>
          !filterData.value.student_sis_id
            ? field.key !== "student_sis_id"
            : field
        )
    })

    const userRole = computed(() => store.getters["authentication/getUserRole"])

    const isQueueModuleActive = computed(() => {
      return store.getters["queueSettings/isQueueModuleActive"]
    })

    const isQueueStatusSelected = computed(() => {
      return store.getters["dashboardTable/isQueueStatusSelected"]
    })

    const isQueueActiveAndStatusSelected = computed(() => {
      return store.getters["dashboardTable/isQueueActiveAndStatusSelected"]
    })

    const showClearQueueButton = computed(() => {
      if (!isQueueActiveAndStatusSelected.value) {
        return false
      }

      if (activePassType.value === "all") {
        return ["admin", "superadmin"].includes(userRole.value)
      } else {
        return true
      }
    })

    const showDownloadCSVButton = computed(() => {
      if (isQueueActiveAndStatusSelected.value) {
        return false
      }
      return true
    })

    const updateQueuePassesTable = computed(
      () => store.getters["dashboardTable/updateQueuePassesTable"]
    )

    let activeLoadAnnouncementId = null
    let loadAnnouncementGeneration = 0
    let appointmentLoadAnnouncementId = null
    let lastStatusText = ""
    let lastStatusAt = 0

    const fetchQueuePasses = async () => {
      const loadId = startLoadAnnouncement()
      try {
        state.isLoading = true
        await store.dispatch("dashboardTable/getDashboardQueuePassesData", {
          type: activePassType.value
        })
        endLoadAnnouncement("Queue passes loaded", loadId)
      } catch (error) {
        cancelActiveLoadAnnouncement()
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while fetching queue passes"
        announceError(errorMessage)
        toast.error(errorMessage)
      } finally {
        state.isLoading = false
      }
    }

    onMounted(async () => {
      // NOTE: do not toggle `state.isLoading` here. The passes loader is owned
      // solely by the pass-fetch watcher below; toggling it here used to clear
      // the loading flag as soon as these (often cached) prerequisite calls
      // resolved, un-gating the table before `dashboardPasses` had arrived and
      // causing a brief "No items available" flash on heavier pass lists.
      await store.dispatch("rooms/getAutoPassLimit")
      /**
       * Removed the below code as the data is already fetched in the view
       * File - src/v3views/DashboardOld.vue
       */
      // store.dispatch("dashboardTable/getDashboard")
      if (!passesTimeSettings.value) {
        await store.dispatch("passes/getPassesTimeSetting")
      }
    })

    watch(
      [() => activePassType?.value, () => activePassState?.value],
      async ([type, status]) => {
        if (
          [
            "appointments_today",
            "appointments_awaiting",
            "appointments_future_awaiting"
          ].includes(type)
        ) {
          return
        }

        if (isQueueActiveAndStatusSelected.value) {
          return await fetchQueuePasses()
        }

        const loadId = startLoadAnnouncement()
        state.isLoading = true
        try {
          // Do NOT fetch time settings here. The backend serializes same-session
          // requests (session lock), so awaiting the settings call ahead of the
          // passes fetch delayed first paint on large pass lists. Settings are
          // loaded separately in onMounted; the min-time (yellow) badge in
          // UserColumnData is guarded to no-op until settings exist, so there is
          // no yellow flash without this await.
          await store.dispatch("dashboardTable/getDashboardPassesData", {
            type,
            status,
            isInitialLoad: state.isInitialLoad
          })
          if (state.isInitialLoad) {
            state.isInitialLoad = false
          }
          endLoadAnnouncement(
            dashboardPasses.value?.length
              ? "Dashboard passes loaded"
              : "No items available",
            loadId
          )
        } finally {
          state.isLoading = false
        }
      },
      { immediate: true }
    )

    watch(
      () => updateQueuePassesTable.value,
      async (value) => {
        if (value) {
          await fetchQueuePasses()
          store.commit("dashboardTable/SET_UPDATE_QUEUE_PASSES_TABLE", false)
        }
      }
    )

    const exportAsCSV = () => {
      announceStatus("Preparing CSV download")
      if (isAppoinmentMode.value) {
        state.isLoading = true
        store
          .dispatch("adultAptPass/getCSVAppointmentPasses", {
            student_sis_id: aptFilterData.value.student_sis_id
          })
          .then((response) => {
            if (response.data) {
              download.CSVExport(response.data, "appointment-passes")
              announceStatus("Appointment passes CSV downloaded")
            }
            state.isLoading = false
          })
          .catch(() => {
            announceError("Unable to download appointment passes CSV")
            state.isLoading = false
          })
      } else {
        state.isLoading = true
        store
          .dispatch("dashboardTable/getDashboardPassesExport")
          .then((response) => {
            if (response.data) {
              download.CSVExport(response.data, "passes")
              announceStatus("Dashboard passes CSV downloaded")
            }
            state.isLoading = false
          })
          .catch(() => {
            announceError("Unable to download dashboard passes CSV")
            state.isLoading = false
          })
      }
    }

    const handleClearPasses = async () => {
      const confirmed = await showConfirmation({
        heading: "Clear Passes",
        headingColor: "red",
        headingIcon: "ri-alert-line",
        body: "Are you sure, this action will end all active passes which exceeds system ended time?<br><br><span class='bold-text'>Confirm Action :</span> This will clear any passes that have exceeded the System Ended time limit.",
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        size: "md"
      })

      if (confirmed) {
        try {
          announceStatus("Clearing passes")
          state.isLoading = true
          await store.dispatch("dashboardTable/clearExceedingPasses")
          await store.dispatch("dashboardTable/getDashboardPassesData")
          announceStatus("Passes cleared successfully")
          toast.success("Passes cleared successfully")
        } catch (error) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Something went wrong while clearing passes"
          announceError(errorMessage)
          toast.error(errorMessage)
        } finally {
          state.isLoading = false
        }
      }
    }

    watch(
      () => state.isVisibleAdvancedSearch,
      () => syncAdvancedSearchA11y()
    )

    watch(isActiveDashboardUpdate, () => syncAdvancedSearchA11y())

    const clearFilter = () => {
      announceStatus("Clearing dashboard filters")
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_students",
          value: []
        }
      ])
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_teachers",
          value: []
        }
      ])
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_rooms",
          value: []
        }
      ])
      store.commit("dashboardTable/SET_DASHBOARD_UPDATES", true)
      state.isLoading = true
      store
        .dispatch("dashboardTable/getDashboardPassesData", {
          isInitialLoad: false
        })
        .then(() => {
          announceStatus("Advanced search selections cleared")
          state.isLoading = false
        })
      syncAdvancedSearchA11y()
    }

    const setPerPage = (option) => {
      if (option) {
        state.isLazyLoadingMode = isAllEntriesPerPageOption(option)
        state.pagination.per_page = option
      }
    }

    const setActivePage = () => {
      helpers.scrollToTop()
    }

    const setActiveRow = (value, index, column, event) => {
      const path = event.path || (event.composedPath && event.composedPath())
      if (state.isBasicView) {
        let isModal = false
        if (path) {
          path.forEach((element) => {
            const classes = element.className
            if (classes && classes == "no-target") {
              isModal = true
            }
          })
        }

        const targetClasses = event.target.classList
        if (!targetClasses.contains("no-target") && !isModal) {
          const dashboardTable = document.getElementById("dashboardTable")
          const rows = dashboardTable.getElementsByTagName("tr")
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i]
            row.classList.remove("active")
          }
          if (
            state.activeTableRow &&
            state.activeTableRow.item.id === value.id
          ) {
            state.activeTableRow = { item: { id: null }, index: null }
          } else {
            state.activeTableRow = { item: value, index }
            if (rows[index + 1]) {
              rows[index + 1].classList.add("active")
            }
          }
        }
      }
    }

    const clearActiveRow = () => {
      const dashboardTable = document.getElementById("dashboardTable")
      const rows = dashboardTable.getElementsByTagName("tr")
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        row.classList.remove("active")
      }
    }

    const sortByColumn = (column) => {
      if (isQueueActiveAndStatusSelected.value) {
        const columnMap = {
          approved_at: "departing_from",
          completed_at: "destination"
        }
        const columnName = columnMap?.[column?.column] || column?.column
        store.commit("dashboardTable/UPDATE_FILTERS_DATA", {
          filter: "sort",
          value: `${columnName}:${column.asc ? "asc" : "desc"}`
        })
        return
      }

      const columnName = state.columnSearchKeys[column.column]
        ? state.columnSearchKeys[column.column].sortBy
        : column.column
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "sort",
          value: `${columnName}:${column.asc ? "asc" : "desc"}`
        }
      ])
    }

    const updateSearchQuery = (value) => {
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", {
        filter: "search_query",
        value: value != "" ? value : null
      })
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "global_search",
          value: 1
        }
      ])
    }

    const syncAdvancedSearchA11y = () => {
      nextTick(() => syncVueSelectListboxAriaHidden())
    }

    const pauseDashboardUpdate = () => {
      store.commit("dashboardTable/SET_DASHBOARD_UPDATES", false)
      announceStatus("School dashboard paused")
      syncAdvancedSearchA11y()
    }

    const searchByQuery = (value) => {
      if (window.searchByQueryTimeout) {
        clearTimeout(window.searchByQueryTimeout)
      }

      if (isQueueActiveAndStatusSelected.value) {
        return updateSearchQuery(value)
      }

      window.searchByQueryTimeout = setTimeout(() => {
        syncAdvancedSearchA11y()
        if (isAppoinmentMode.value) {
          store.commit("adultAptPass/SET_SEARCH_QUERY", value)
          updateSearchQuery(value)
          store.dispatch("adultAptPass/getAppointmentPasses")
        } else {
          const loadId = startLoadAnnouncement()
          updateSearchQuery(value)
          state.isLoading = true
          store
            .dispatch("dashboardTable/getDashboardPassesData", {
              isInitialLoad: false
            })
            .then(() => {
              setTimeout(() => {
                state.isLoading = false
                endLoadAnnouncement(
                  dashboardPasses.value?.length
                    ? "Dashboard passes loaded"
                    : "No items available",
                  loadId
                )
              }, 500)
            })
            .catch(() => {
              state.isLoading = false
              cancelActiveLoadAnnouncement()
            })
        }
        if (isActiveDashboardUpdate.value == true) {
          store.commit("dashboardTable/SET_DASHBOARD_UPDATES", false)
        }
      }, 500)
    }

    const getPassStatus = (pass) => {
      if (pass.badge_flags) {
        if (pass.badge_flags.active && !pass.badge_flags.min_time) {
          return { label: "Active", class: "active" }
        }
        if (pass.badge_flags.ended) {
          return { label: "Ended", class: "ended" }
        }
        if (pass.badge_flags.system_completed) {
          return { label: "System Ended", class: "system-completed" }
        }
        if (pass.badge_flags.min_time) {
          return {
            label: passesTimeSettings.value
              ? helpers.formatDate(
                  passesTimeSettings.value.min_time,
                  "hh:mm:ss",
                  "m"
                ) + "+ MIN"
              : "Min time",
            class: "min-time"
          }
        }
        if (pass.badge_flags.extended) {
          return { label: "Extended", class: "extended" }
        }
        if (pass.badge_flags.missed) {
          return { label: "Missed", class: "missed" }
        }
        if (pass.badge_flags.canceled) {
          return { label: "Canceled", class: "canceled" }
        }
      }
      return ""
    }

    const advancedFilter = () => {
      announceStatus("Applying dashboard filters")
      store.commit("dashboardTable/SET_DASHBOARD_UPDATES", false)
      state.isLoading = true
      store
        .dispatch("dashboardTable/getDashboardPassesData", {
          isInitialLoad: false
        })
        .then(() => {
          announceStatus(
            dashboardPasses.value?.length
              ? "Dashboard filters applied"
              : "No items available"
          )
          state.isLoading = false
          syncAdvancedSearchA11y()
        })
    }

    const resumeDashboardUpdate = async () => {
      announceStatus("Resuming dashboard updates")
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_students",
          value: []
        }
      ])
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_teachers",
          value: []
        }
      ])
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_rooms",
          value: []
        }
      ])
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", {
        filter: "search_query",
        value: ""
      })
      store.commit("adultAptPass/SET_SEARCH_QUERY", "")
      store.commit("dashboardTable/SET_DASHBOARD_UPDATES", true)
      if (isAppoinmentMode.value) {
        store.dispatch("adultAptPass/getAppointmentPasses")
      } else {
        if (isQueueActiveAndStatusSelected.value) {
          return await fetchQueuePasses()
        }
        store.dispatch("dashboardTable/getDashboardPassesData", {
          isInitialLoad: false
        })
      }
    }

    const focusFirstVisibleDashboardControl = () => {
      const region = tabsRegionRef.value
      if (!region) return

      const focusableSelector =
        "button:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])"
      const firstVisibleFocusable = Array.from(
        region.querySelectorAll(focusableSelector)
      ).find((element) => {
        const computedStyle = window.getComputedStyle(element)
        const hasLayoutBox = element.offsetWidth > 0 || element.offsetHeight > 0
        return (
          hasLayoutBox &&
          computedStyle.visibility !== "hidden" &&
          computedStyle.display !== "none"
        )
      })

      firstVisibleFocusable?.focus?.()
    }

    const toggleDashboardTabs = async () => {
      state.showTabs = !state.showTabs
      await nextTick()

      if (state.showTabs) {
        focusFirstVisibleDashboardControl()
      } else {
        mobileTabsToggleButtonRef.value?.focus?.()
      }
    }

    const onClearQueue = async () => {
      const { showConfirmation } = useConfirmation(modal)

      const body =
        activePassType?.value === "all"
          ? "Are you sure you would like to clear <span class='bold-text'>ALL PASS QUEUES</span> in the school? This would remove <span class='bold-text'>ALL PENDING REQUESTS</span> in queues and cannot be reversed."
          : "Are you sure you would like to clear all pass requests in your queues?"

      const shouldClear = await showConfirmation({
        heading: "Please confirm",
        headingIcon: "ri ri-alert-line",
        headingColor: "red",
        headerPlacement: "start",
        body,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        cancelButtonSolid: false,
        buttonPlacement: "end"
      })

      if (!shouldClear) return

      try {
        announceStatus("Clearing queue passes")
        await store.dispatch("dashboardTable/clearQueuePasses", {
          type: activePassType.value
        })
        announceStatus("Queue passes cleared successfully")
        toast.success(
          activePassType?.value === "all"
            ? "All the school queue passes cleared successfully"
            : "Your queue passes cleared successfully"
        )
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while clearing queue passes"
        announceError(message)
        toast.error(message)
      }
    }

    function announceStatus(message) {
      if (!message) return
      const now = Date.now()
      if (message === lastStatusText && now - lastStatusAt < 600) {
        return
      }
      lastStatusText = message
      lastStatusAt = now
      announce(message)
    }

    function startLoadAnnouncement() {
      if (activeLoadAnnouncementId != null) {
        return activeLoadAnnouncementId
      }
      loadAnnouncementGeneration += 1
      activeLoadAnnouncementId = loadAnnouncementGeneration
      announceStatus("Loading")
      return activeLoadAnnouncementId
    }

    function endLoadAnnouncement(message, loadId) {
      if (loadId == null || loadId !== activeLoadAnnouncementId) {
        return
      }
      activeLoadAnnouncementId = null
      announceStatus(message)
    }

    function cancelActiveLoadAnnouncement() {
      activeLoadAnnouncementId = null
      loadAnnouncementGeneration += 1
    }

    function announceError(message) {
      if (!message) return
      announce(message, { assertive: true })
    }

    watch(isAppoinmentMode, (inAppointmentMode, wasInAppointmentMode) => {
      if (wasInAppointmentMode && !inAppointmentMode) {
        appointmentLoadAnnouncementId = null
        cancelActiveLoadAnnouncement()
      }
      if (inAppointmentMode && !wasInAppointmentMode) {
        cancelActiveLoadAnnouncement()
      }
    })

    watch(areAppointmentPassesLoading, (loading, wasLoading) => {
      if (loading && wasLoading === false && isAppoinmentMode.value) {
        if (appointmentLoadAnnouncementId == null) {
          appointmentLoadAnnouncementId = startLoadAnnouncement()
        }
        syncAdvancedSearchA11y()
        return
      }
      if (!loading && wasLoading && appointmentLoadAnnouncementId != null) {
        const loadId = appointmentLoadAnnouncementId
        appointmentLoadAnnouncementId = null
        nextTick(() => {
          if (!isAppoinmentMode.value) {
            return
          }
          endLoadAnnouncement(
            appointmentPasses.value.length
              ? "Appointment passes loaded"
              : "No items available",
            loadId
          )
          syncAdvancedSearchA11y()
        })
      }
    })

    return {
      isQueueModuleActive,
      isPassStatusQueue: queueUtils.isPassStatusQueue,
      isQueueStatusSelected,
      isQueueActiveAndStatusSelected,
      showClearQueueButton,
      showDownloadCSVButton,
      filterDataStudentNumber,
      state,
      periods,
      editablePass,
      passesTimeSettings,
      areAppointmentPassesLoading,
      appointmentPasses,
      isDashboardBusy,
      isAppoinmentMode,
      currentUser,
      filterData,
      aptFilterData,
      dashboardPasses,
      isActiveDashboardUpdate,
      activeSchoolHasAppoitmentPasses,
      isUserInTransparency,
      stats,
      activePassType,
      activePassState,
      school,
      activeDataTableFields,
      exportAsCSV,
      clearFilter,
      setPerPage,
      setActivePage,
      setActiveRow,
      clearActiveRow,
      sortByColumn,
      searchByQuery,
      getPassStatus,
      advancedFilter,
      resumeDashboardUpdate,
      pauseDashboardUpdate,
      onClearQueue,
      handleClearPasses,
      tabsRegionRef,
      mobileTabsToggleButtonRef,
      toggleDashboardTabs
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/_variables.scss";

.table-tabs-header button:focus-visible,
.table-tabs-header :deep(button:focus-visible),
.table-tabs-header :deep(a:focus-visible),
.table-tabs-header :deep(.v3-base-btn:focus-visible),
.table-tabs-header :deep(.vs__dropdown-toggle:focus-within),
.status-guides-container :deep(button:focus-visible),
.status-guides-container :deep(a:focus-visible) {
  outline: 2px solid $outline-color;
  outline-offset: $outline-offset;
}
</style>
